'use client';

import { MoreHorizontal } from 'lucide-react';

import type {
  CareStateRequestStatusT,
  CareStateRequestUrgencyT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { Button } from '@/shared/ui/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';

import { UrgencyPill } from './UrgencyPill';

const STATUS_OPTIONS = [
  'Open',
  'Pending Follow Up',
  'Resolved',
  'Cancelled',
] as const satisfies ReadonlyArray<CareStateRequestStatusT>;

const URGENCY_OPTIONS = [
  'Routine',
  'Urgent',
] as const satisfies ReadonlyArray<CareStateRequestUrgencyT>;

export interface CareStateRequestRowProps {
  requestId: string;
  title: string;
  status: CareStateRequestStatusT;
  urgency: CareStateRequestUrgencyT;
  onSetStatus(requestId: string, next: CareStateRequestStatusT): void;
  onSetUrgency(requestId: string, next: CareStateRequestUrgencyT): void;
}

export function CareStateRequestRow({
  requestId,
  title,
  status,
  urgency,
  onSetStatus,
  onSetUrgency,
}: CareStateRequestRowProps) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <UrgencyPill urgency={urgency} />
      <div className="min-w-0 flex-1 truncate text-base font-medium text-foreground" title={title}>
        {title}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Edit request: ${title}`}
            className="-mr-1 h-7 w-7 rounded text-foreground hover:bg-muted/40"
          >
            <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-40">
          <DropdownMenuLabel>Status</DropdownMenuLabel>
          {STATUS_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option}
              onSelect={() => {
                if (option !== status) {
                  onSetStatus(requestId, option);
                }
              }}
              aria-checked={option === status}
            >
              {option}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Urgency</DropdownMenuLabel>
          {URGENCY_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option}
              onSelect={() => {
                if (option !== urgency) {
                  onSetUrgency(requestId, option);
                }
              }}
              aria-checked={option === urgency}
            >
              {option}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
